import React, { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { motion, useScroll, useTransform } from 'motion/react'
import { easeInOut } from '@popmotion/easing'
import { useMediaQuery } from 'react-responsive'
import { Spacing } from '@kvika/audur-theme'
import { DisruptionModeStrings } from '@kvika/audur-strings'
import { decodeBase64String } from '@kvika/string-utils'

import GridContainer from '../../components/grid-container/GridContainer'
import SiteAlert from '../../components/site-alert/SiteAlert'
import TopNavigation from '../top-navigation/TopNavigation'
import Footer from '../footer/Footer'
import useOrientationChange from '../../utils/hooks/useOrientationChange'
import { getNavigationHeaderY } from '../../utils/Constants'
import { MetaTagInfo, ServiceStatusMode, SiteAlert as SiteAlertType } from '../../utils/types/Types'
import { getKvikaApiClient } from '../../utils/Utils'
import { AudurFooter, AudurHeader, AudurLink } from '../../prismic/prismicTypes'

import MetaTags from './meta-tags/MetaTags'
import styles from './Layout.module.scss'

const MobileMenu = dynamic(() => import('../../components/mobileMenu/MobileMenu'), { ssr: false })

type Props = {
	children: JSX.Element | JSX.Element[]
	title?: React.ReactNode | React.ReactNode[] | null
	metaTags?: MetaTagInfo
	footer: AudurFooter
	header: AudurHeader
	siteAlert?: SiteAlertType
}

const Layout = (props: Props) => {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const [serviceStatusMode, setServiceStatusMode] = useState<ServiceStatusMode>()
	const navigationRef = useRef<HTMLDivElement>(null)
	const { scrollYProgress } = useScroll()
	const orientationDimensions = useOrientationChange()
	const isMobile = useMediaQuery({ query: '(max-width: 599px)' })
	const router = useRouter()
	const transformedHeaderY = useTransform(scrollYProgress, [0, 0.1], [0, -getNavigationHeaderY(isMobile)], {
		ease: easeInOut,
	})

	const apiClient = getKvikaApiClient(setServiceStatusMode)

	const { title, metaTags, footer, header, siteAlert, children } = props

	useEffect(() => {
		if (isMobileMenuOpen) {
			// If orientation is changed with the mobile menu open, toggle it closed
			setIsMobileMenuOpen(false)
		}
	}, [orientationDimensions])

	useEffect(() => {
		// Dummy call to trigger disruption-mode header check; result is intentionally ignored
		apiClient.getCountries().catch(() => {
			// API may be unavailable — not an error worth surfacing
		})
	}, [])

	const getLinksForMobileMenu = () => {
		const buttons = header?.button_group?.map((button) => button.button as AudurLink) ?? []
		const links = header?.link_group?.map((link) => link.link as AudurLink) ?? []
		return buttons.concat(links)
	}

	return (
		<>
			<Head>
				{title && <title>{title}</title>}
				{metaTags && (
					<MetaTags
						title={metaTags.title}
						description={metaTags.description}
						url='https://audur.is'
						imageUrl={metaTags.imageUrl}
					/>
				)}
			</Head>
			{siteAlert && <SiteAlert alert={siteAlert} />}
			{serviceStatusMode?.serviceStatus && process.env.NEXT_PUBLIC_DISABLE_DISRUPTION_NOTIFICATIONS !== 'true' && (
				<motion.div className={styles.topInfoBanner}>
					<div>
						{serviceStatusMode?.message
							? decodeBase64String(serviceStatusMode.message)
							: DisruptionModeStrings.DisruptionModeText}
					</div>
				</motion.div>
			)}
			{header && (
				<motion.div ref={navigationRef} className={styles.navigation} style={{ translateY: transformedHeaderY }}>
					<TopNavigation
						onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
						isMobileMenuOpen={isMobileMenuOpen}
						header={header}
					/>
				</motion.div>
			)}

			<motion.div className={styles.pageContent}>
				<motion.div className={styles.content}>{children}</motion.div>
				<div className={styles.footer}>
					<GridContainer
						style={
							isMobile
								? { paddingTop: Spacing.XLarge, paddingBottom: Spacing.XLarge }
								: { paddingTop: Spacing.Medium, paddingBottom: Spacing.Medium }
						}
					>
						{footer && <Footer footer={footer} />}
					</GridContainer>
				</div>
				<MobileMenu
					activeRoute={router.pathname}
					visible={isMobileMenuOpen}
					links={getLinksForMobileMenu()}
					navigationRef={navigationRef}
				/>
			</motion.div>
		</>
	)
}

export default Layout
